import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import deleteIcon from "../../../assets/deleteIcon.svg";
import copyIcon from "../../../assets/copyIcon.svg";
import clipboardIcon from "../../../assets/clipboard.svg";
import cutIcon from "../../../assets/cutIcon.svg";
import pasteIcon from "../../../assets/pasteIcon.svg"
import VerticalSpace from "../../VerticalSpace";
import Label from "../../Common/Label";
import editIcon from "../../../assets/editIcon.svg";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import "../../../stylesheets/theme.css";
import {
  baseURL,
  deleteRequest,
  getRequest,
  multiPostRequest,
  postRequest,
} from "../../../api/apiClient";
import Toast from "../../Common/Toast";
import DeleteConfirmation from "../../Common/DeleteConfirmation";
import Loader from "../../../utils/Loading";
import gsap from "gsap";
import "../../../stylesheets/common.css";
import DeleteLoader from "../../../utils/deleteLoader";
import RowContainer from "../../Common/RowContainer";
import Switch from "../../Common/Switch";
import TextEditor, { TextEditorRef } from "../../Common/TextEditor";
import NestedMenu from "../../ParticlesDropdown/NestedMenu";
import HorizontalSpace from "../../HorizontalSpace";
import {
  ensureParticleValue1AtEnd,
  ensureParticleValueAtStart,
  getParticleReadOnlyStatus,
  particlePairValues,
} from "../../../utils/particlePairUtils";
import { DropdownValue, Pair } from "../../../types";
import { useDispatch, useSelector } from "react-redux";
import {
  copyToClipboard,
  cutToClipboard,
  clearClipboard,
  selectClipboard,
} from "../../../redux/clipboardSlice";

interface SubTheme {
  id: number;
  name: string;
  cards: number;
  order?: number;
}

const SubThemes = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const clipboard = useSelector(selectClipboard);
  const [addSubThemes, setNewThemes] = useState(false);
  const [isPasting, setIsPasting] = useState(false);
  const [selectedSubThemes, setSelectedSubThemes] = useState<number[]>([]);
  const [themeName, setThemeName] = useState("");
  const [intro, setIntro] = useState("");
  const [introTitle, setIntroTitle] = useState("");
  const introTitleEditorRef = useRef<TextEditorRef>(null);
  const introEditorRef = useRef<TextEditorRef>(null);
  const [subthemes, setSubThemes] = useState<SubTheme[]>([]);
  const [editSubThemes, setEditSubThemes] = useState<SubTheme | null>(null);
  const [subThemeError, setSubThemeError] = useState("");
  const [parentThemeName, setParentThemeName] = useState("");
  const [deleteDialogMessage, setDeleteDialogMessage] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [onConfirmDelete, setOnConfirmDelete] = useState<() => void>(() => { });
  const [subThemeToDelete, setSubThemeToDelete] = useState<number | null>(null);
  const [pairs, setPairs] = useState<Pair[]>([]);
  const [startingLessonImage, setStartingLessonImage] = useState("");
  const [startingLessonImageLoading, setStartingLessonImageLoading] =
    useState(false);
  const [pairToDelete, setPairToDelete] = useState<{
    index: number;
    id?: number;
  } | null>(null);
  const [validationError, setValidationError] = useState<{
    pairs: {
      [key: number]: {
        dropdownValue?: string;
        inputValue?: string;
      };
    };
  }>({
    pairs: {},
  });
  const [errors, setErrors] = useState({
    title: "",
    startingLessonImage: "",
    // myLessonsImage: "",
    introTitle: "",
    // intro: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [myLessonsImage, setMyLessonsImage] = useState("");
  // const [myLessonsImageLoading, setMyLessonsImageLoading] = useState(false);
  const [isWhiteBackground, setIsWhiteBackground] = useState(false);
  const [isFree, setIsFree] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [isContinueLearning, setIsContinueLearning] = useState(false);
  const [isFreshLearning, setIsFreshLearning] = useState(false);
  const startingLessonInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const myLessonsInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { themeId: themeId } = location.state || {};

  useEffect(() => {
    const fetchSubThemes = async () => {
      try {
        setIsLoading(true);
        const response = await getRequest(
          `${baseURL}/lesson/get-subthemes/${id}`
        );

        const SubThemeData = response?.data.data.subTheme;
        const IntroData = response?.data.data.themeData.themes;
        const particles = response?.data.data.themeData.sortedParticles;

        const handlecardsCount = SubThemeData.map(async (subTheme: any) => {
          const reqData = {
            modelName: "flashcards",
            field: "subthemeId",
            id: subTheme.id.toString(),
          };
          const cardCountResponse = await postRequest(
            `${baseURL}/lesson/counts`,
            reqData
          );
          if (cardCountResponse) {
            return { ...subTheme, cards: cardCountResponse.data.data.count };
          } else {
            return { ...subTheme, cards: 0 };
          }
        });
        const resolvedCardsCount = await Promise.all(handlecardsCount);
        setSubThemes(resolvedCardsCount);
        setParentThemeName(IntroData.name);
        setStartingLessonImage(IntroData.startingLessonImage);
        setIntro(IntroData.intro);
        setIntroTitle(IntroData.introTitle);
        setMyLessonsImage(IntroData.myLessonsImage);
        setIsWhiteBackground(IntroData.isWhiteBackground);
        setIsFree(IntroData.isFree || false);
        setIsSelected(IntroData.isSelected || false);
        setIsContinueLearning(IntroData.isContinueLearning || false);
        setIsFreshLearning(IntroData.isFreshLearning || false);
        const fetchedPairs = await Promise.all(
          particles.map(async (item: any, index: number) => {
            const particleData = await fetchParticleById(item.type);
            return {
              id: item.id,
              dropdownValue: {
                id: particleData.id,
                name: particleData.name,
                pId: particleData.pId,
                particleType: particleData.particleType,
                particleValue: particleData.particleValue,
                particleType1: particleData.particleType1,
                particleValue1: particleData.particleValue1,
                children: particleData.children || [],
              },
              inputValue: item.value,
              order: item.order || index + 1,
              isReadOnly: getParticleReadOnlyStatus(particleData),
            };
          })
        );
        setPairs(fetchedPairs);

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching sub-themes:", error);
      }
    };

    fetchSubThemes();
  }, [id]);

  const fetchParticleById = async (id: number) => {
    try {
      const response = await getRequest(
        `${baseURL}/particle/get-particleById/${id}`
      );
      return response?.data.data;
    } catch (error) {
      console.error("Error fetching particle by ID:", error);
      throw error;
    }
  };

  const handleNewSubThemes = () => {
    setThemeName("");
    setSubThemeError("");
    setNewThemes(true);
    setEditSubThemes(null);
  };

  // const handleIntro = (value: string) => {
  //   setIntro(value);
  // };

  const handleIntroTitle = (value: string) => {
    setIntroTitle(value);
  };

  const addNewPair = () => {
    const reorderedPairs = pairs
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((pair, index) => ({
        ...pair,
        order: index + 1,
      }));

    const newPair = {
      dropdownValue: null,
      inputValue: "",
      order: reorderedPairs.length + 1,
    };
    setPairs([...reorderedPairs, newPair]);
  };

  const handleInputChange = (index: number, value: string) => {
    const updatedPairs = [...pairs];
    const pair = updatedPairs[index];

    // Handle particleValue (black type) - should be at the beginning and fixed
    if (pair.dropdownValue?.particleType === "black" && pair.dropdownValue?.particleValue) {
      value = ensureParticleValueAtStart(
        value,
        pair.dropdownValue.particleValue
      );
    }

    // Handle particleValue1 (other types) - should be at the end
    // BUT skip if particleValue1 is "..." (placeholder for user input)
    if (pair.dropdownValue?.particleValue1 && pair.dropdownValue.particleValue1 !== "...") {
      value = ensureParticleValue1AtEnd(
        value,
        pair.dropdownValue.particleValue1
      );
    }

    updatedPairs[index].inputValue = value;

    const updatedErrors = { ...validationError };
    if (updatedErrors.pairs[index]) {
      updatedErrors.pairs[index].inputValue = "";
    } else {
      updatedErrors.pairs[index] = { inputValue: "" };
    }

    setPairs(updatedPairs);
    setValidationError(updatedErrors);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    const pair = pairs[index];
    const input = e.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;

    if (pair.dropdownValue?.particleType === "black" && pair.dropdownValue?.particleValue) {
      const fixedValue = pair.dropdownValue.particleValue.trim();
      const fixedLength = fixedValue.length;

      // Prevent deletion of the fixed particle value at the beginning
      if (
        (e.key === 'Backspace' && cursorPosition <= fixedLength) ||
        (e.key === 'Delete' && cursorPosition < fixedLength) ||
        (e.key === 'ArrowLeft' && cursorPosition <= fixedLength && (e.ctrlKey || e.metaKey)) ||
        (cursorPosition < fixedLength && e.key.length === 1) // Prevent typing within the fixed part
      ) {
        e.preventDefault();
        // Move cursor to the end of fixed value
        setTimeout(() => {
          input.setSelectionRange(fixedLength, fixedLength);
        }, 0);
      }
    }
  };

  const handleDropdownChange = (index: number, item: DropdownValue) => {
    const updatedPairs: Pair[] = [...pairs];

    particlePairValues(item, index, updatedPairs);

    const updatedErrors = { ...validationError };
    if (item.particleValue || item.particleValue1) {
      delete updatedErrors.pairs[index];
    } else if (updatedErrors.pairs[index]) {
      updatedErrors.pairs[index].dropdownValue = "";
    } else {
      updatedErrors.pairs[index] = { dropdownValue: "" };
    }
    setPairs(updatedPairs);
    setValidationError(updatedErrors);
  };

  // const toggleSwitch = () => {
  //   setIsWhiteBackground((prev) => !prev);
  // };

  const handlePublishClick = async () => {
    let hasError = false;

    // Get current values from editor refs (bypasses debounce)
    const currentIntroTitle = introTitleEditorRef.current?.getCurrentValue() || introTitle || "";
    const currentIntro = introEditorRef.current?.getCurrentValue() || intro || "";

    const validationErrors = {
      title: "",
      startingLessonImage: !startingLessonImage
        ? "starting lesson image is required"
        : "",
      // myLessonsImage: !myLessonsImage ? "my lessons image is required" : "",
      introTitle: !currentIntroTitle.trim() ? "Intro title is required" : "",
      // intro: !currentIntro ? "Intro content is required" : "",
    };

    setErrors(validationErrors);

    // If any validation error exists, stop the form submission
    if (Object.values(validationErrors).some((error) => error)) {
      return;
    }

    if (pairs.length === 0) {
      Toast("Please add at least one intro partículas.", "error");
      return;
    }

    const newErrorsColumn1: {
      [key: number]: { dropdownValue?: string; inputValue?: string };
    } = {};
    pairs.forEach((item, index) => {
      newErrorsColumn1[index] = {};
      if (!item.dropdownValue) {
        newErrorsColumn1[index].dropdownValue = "required";
        hasError = true;
      }
      if (!item.inputValue) {
        newErrorsColumn1[index].inputValue = "required";
        hasError = true;
      }
    });

    setValidationError((prevErrors) => ({
      ...prevErrors,
      pairs: newErrorsColumn1,
    }));

    if (hasError) {
      return;
    }
    setIsLoading(true);
    const payload = {
      themeId: themeId || id,
      isFree: isFree,
      isSelected: isSelected,
      isContinueLearning: isContinueLearning,
      isFreshLearning: isFreshLearning,
      introTitle: currentIntroTitle,
      intro: currentIntro,
      startingLessonImage: startingLessonImage || null,
      myLessonsImage: myLessonsImage || null,
      isWhiteBackground: isWhiteBackground,
      particles: pairs.map((pair) => ({
        id: pair.id || null,
        type: pair.dropdownValue?.id || null,
        value: pair.inputValue || "",
        order: pair.order || 0,
      })),
    };

    const response = await postRequest(
      `${baseURL}/lesson/create-update-themeintro`,
      payload
    );
    if (response?.data.success) {
      const themesData = response?.data.data.theme;
      const sortedParticles = response?.data.data.sortedParticles;

      setIntro(themesData.intro);
      setIntroTitle(themesData.introTitle);
      setStartingLessonImage(themesData.startingLessonImage);
      setMyLessonsImage(themesData.myLessonsImage);
      setIsWhiteBackground(themesData.isWhiteBackground);
      setIsFree(themesData.isFree || false);
      setIsSelected(themesData.isSelected || false);
      setIsContinueLearning(themesData.isContinueLearning || false);
      setIsFreshLearning(themesData.isFreshLearning || false);
      const fetchedPairs = await Promise.all(
        sortedParticles.map(async (item: any, index: number) => {
          const particleData = await fetchParticleById(item.type);
          return {
            id: item.id,
            dropdownValue: {
              id: particleData.id,
              name: particleData.name,
              pId: particleData.pId,
              particleType: particleData.particleType,
              particleValue: particleData.particleValue,
              particleType1: particleData.particleType1,
              particleValue1: particleData.particleValue1,
              children: particleData.children || [],
            },
            inputValue: item.value,
            order: item.order || index + 1,
            isReadOnly: getParticleReadOnlyStatus(particleData),
          };
        })
      );
      setPairs(fetchedPairs);
      setIsLoading(false);
      Toast(response?.data.statusMessage, "success");
    }
    setIsLoading(false);
  };

  const handleSaveSubTheme = async () => {
    if (!themeName) {
      setSubThemeError("Please enter sub-theme name");
      return;
    }
    const activeThemeId = themeId || id;

    if (editSubThemes) {
      await handleUpdateSubTheme(editSubThemes.id, themeName);
    } else {
      await handleCreateSubTheme(activeThemeId, themeName);
    }
  };

  const handleCreateSubTheme = async (
    themeId: number,
    subThemeName: string
  ) => {
    try {
      const nextOrder = subthemes.length + 1;

      const reqData = {
        themeId,
        name: subThemeName,
        order: nextOrder,
      };
      setIsLoading(true);
      const response = await postRequest(
        `${baseURL}/lesson/create-subtheme`,
        reqData
      );

      const newTheme: SubTheme = {
        id: response?.data.data.id, // Generate a unique ID (you might want to use a more robust approach)
        name: response?.data.data.name,
        cards: 0, // Initialize cards count
        order: response?.data.data.order,
      };
      setSubThemes([...subthemes, newTheme]);
      setThemeName("");
      setNewThemes(false);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setSubThemeError("Error creating Sub-Theme");
    }
  };

  const handleUpdateSubTheme = async (
    subthemeId: number,
    subThemeName: string
  ) => {
    try {
      const reqData = {
        name: subThemeName,
      };
      setIsLoading(true);
      const response = await postRequest(
        `${baseURL}/lesson/update-subthemes/${subthemeId}`,
        reqData
      );
      setIsLoading(false);

      const updatedSubThemes = subthemes.map((subTheme) =>
        subTheme.id === subthemeId
          ? { ...subTheme, name: subThemeName }
          : subTheme
      );
      setSubThemes(updatedSubThemes);
      setThemeName("");
      setNewThemes(false);
      setEditSubThemes(null);
      Toast(response?.data.statusMessage, "success");
    } catch (error) {
      setIsLoading(false);
      setSubThemeError("Error updateing Sub-Theme");
    }
  };

  const handleEditSubTheme = (subTheme: SubTheme) => {
    setThemeName(subTheme.name);
    setEditSubThemes(subTheme);
    setNewThemes(true);
  };

  const handleAddFlashcard = (id: number, name: string) => {
    navigate(`/lessons/theme/subthemes/${id}`, {
      state: { subThemeId: id, subtheme: name },
    });
  };

  const handleBackClick = async () => {
    try {
      const response = await getRequest(
        `${baseURL}/lesson/get-fetch-themes/${id}`
      );

      const lessonId = response?.data.data[0].lessonId;
      navigate(`/lessons/edit-lessons/${lessonId}`);
    } catch (error) {
      console.error("Error fetching themeId:", error);
    }
  };

  const handleConfirmDeleteSubTheme = async (id: number) => {
    try {
      setLoading(true);
      const response = await deleteRequest(
        `${baseURL}/lesson/delete-subthemes/${id}`
      );
      setLoading(false);
      const updatedSubThemes = subthemes.filter((theme) => theme.id !== id);
      setSubThemes(updatedSubThemes);
      setShowDeleteDialog(false);
      setSubThemeToDelete(null);
      Toast(response?.data.statusMessage, "success");
    } catch (error) {
      console.error("Error deleting flashcard:", error);
      setSubThemeError("Error deleting flashcard");
      setLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const handleDeleteSubTheme = (id: number, name: string) => {
    setSubThemeToDelete(id);
    setDeleteDialogMessage(`Are you sure you want to delete this "${name}"?`);
    setOnConfirmDelete(() => () => handleConfirmDeleteSubTheme(id));
    setShowDeleteDialog(true);
  };

  /** Duplicate sub-theme in-place immediately — original shortcut behavior */
  const handleDuplicateSubTheme = async (subThemeId: number) => {
    try {
      const response = await postRequest(`${baseURL}/lesson/clone-subtheme`, { id: subThemeId });
      if (response?.data.success) {
        Toast(response.data.statusMessage || "Sub-theme duplicated successfully", "success");
        const cloned: any = response.data.data;
        const cardCountResponse = await postRequest(`${baseURL}/lesson/counts`, {
          modelName: "flashcards",
          field: "subthemeId",
          id: cloned.id.toString(),
        });
        const cardsCount = cardCountResponse?.data?.data?.count ?? 0;
        setSubThemes((prev) => [
          ...prev,
          { id: cloned.id, name: cloned.name, cards: cardsCount, order: cloned.order },
        ]);
      } else {
        Toast("Failed to duplicate sub-theme", "error");
      }
    } catch (error) {
      console.error("Error duplicating sub-theme:", error);
      Toast("Failed to duplicate sub-theme", "error");
    }
  };

  /** Copy sub-theme to global clipboard — no server call yet */
  const handleCopySubTheme = (subThemeId: number) => {
    const subTheme = subthemes.find((s) => s.id === subThemeId);
    if (!subTheme) return;
    dispatch(
      copyToClipboard({
        items: [{ id: subTheme.id, name: subTheme.name }],
        itemType: "subtheme",
        sourceParentId: Number(id),
      })
    );
    Toast(`"${subTheme.name}" copied. Navigate to any theme and click Paste.`, "success");
  };

  /** Cut sub-theme to global clipboard — no server call yet */
  const handleCutSubTheme = (subThemeId: number) => {
    const subTheme = subthemes.find((s) => s.id === subThemeId);
    if (!subTheme) return;
    dispatch(
      cutToClipboard({
        items: [{ id: subTheme.id, name: subTheme.name }],
        itemType: "subtheme",
        sourceParentId: Number(id),
      })
    );
    Toast(`"${subTheme.name}" cut. Navigate to the destination theme and click Paste.`, "success");
  };

  /** Toggle a single sub-theme in the selection set */
  const toggleSubThemeSelection = (subThemeId: number) => {
    setSelectedSubThemes((prev) =>
      prev.includes(subThemeId) ? prev.filter((x) => x !== subThemeId) : [...prev, subThemeId]
    );
  };

  /** Bulk copy all selected sub-themes to clipboard */
  const handleCopySelectedSubThemes = () => {
    if (selectedSubThemes.length === 0) return;
    const items = subthemes
      .filter((s) => selectedSubThemes.includes(s.id))
      .map((s) => ({ id: s.id, name: s.name }));
    dispatch(
      copyToClipboard({
        items,
        itemType: "subtheme",
        sourceParentId: Number(id),
      })
    );
    Toast(`${items.length} sub-theme(s) copied to clipboard.`, "success");
    setSelectedSubThemes([]);
  };

  /** Bulk cut all selected sub-themes to clipboard */
  const handleCutSelectedSubThemes = () => {
    if (selectedSubThemes.length === 0) return;
    const items = subthemes
      .filter((s) => selectedSubThemes.includes(s.id))
      .map((s) => ({ id: s.id, name: s.name }));
    dispatch(
      cutToClipboard({
        items,
        itemType: "subtheme",
        sourceParentId: Number(id),
      })
    );
    Toast(`${items.length} sub-theme(s) cut. Navigate to the destination theme and click Paste.`, "success");
    setSelectedSubThemes([]);
  };

  /**
   * Paste clipboard sub-themes into THIS theme.
   * Copy → clones here (original untouched).
   * Cut  → if destination is a DIFFERENT theme: clone here then delete original.
   *         if destination is the SAME theme: nothing changes (item stays as-is).
   */
  const handlePasteSubThemes = async () => {
    if (clipboard.itemType !== "subtheme" || clipboard.items.length === 0) return;
    const targetThemeId = themeId || Number(id);
    if (!targetThemeId) {
      Toast("Cannot determine the target theme ID.", "error");
      return;
    }

    // If cut + pasting back into the same theme → just cancel the cut, nothing moves
    if (clipboard.action === "cut" && clipboard.sourceParentId === targetThemeId) {
      dispatch(clearClipboard());
      Toast("Pasted back into the same theme — item restored in place.", "success");
      return;
    }

    setIsPasting(true);
    try {
      for (const item of clipboard.items) {
        const response = await postRequest(`${baseURL}/lesson/clone-subtheme`, {
          id: item.id,
          targetThemeId,
        });
        if (response?.data.success) {
          const cloned: any = response.data.data;
          const cardCountResponse = await postRequest(`${baseURL}/lesson/counts`, {
            modelName: "flashcards",
            field: "subthemeId",
            id: cloned.id.toString(),
          });
          const cardsCount = cardCountResponse?.data?.data?.count ?? 0;
          setSubThemes((prev) => [
            ...prev,
            { id: cloned.id, name: cloned.name, cards: cardsCount, order: cloned.order },
          ]);
        } else {
          Toast(`Failed to paste "${item.name}"`, "error");
        }
      }
      if (clipboard.action === "cut") {
        // Delete originals only when moving to a DIFFERENT theme
        for (const item of clipboard.items) {
          await deleteRequest(`${baseURL}/lesson/delete-subthemes/${item.id}`);
          // Remove the dimmed original from the current view if we are on the source theme
          setSubThemes((prev) => prev.filter((s) => s.id !== item.id));
        }
        Toast(`${clipboard.items.length} sub-theme(s) moved successfully.`, "success");
      } else {
        Toast(`${clipboard.items.length} sub-theme(s) pasted successfully.`, "success");
      }
      dispatch(clearClipboard());
    } catch (error) {
      console.error("Error pasting sub-themes:", error);
      Toast("Failed to paste sub-themes", "error");
    } finally {
      setIsPasting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setSubThemeToDelete(null);
  };

  const removePair = (index: number) => {
    const updatedPairs = [...pairs];
    updatedPairs.splice(index, 1);
    setPairs(updatedPairs);
  };

  const handleDeletePair = (index: number, id?: number) => {
    if (id !== undefined) {
      setPairToDelete({ index, id });
      setDeleteDialogMessage("Are you sure you want to delete this pair?");
      setOnConfirmDelete(() => () => handleConfirmDeletePair(index, id)); // Pass index and ID to confirm function
      setShowDeleteDialog(true);
    } else {
      removePair(index);
    }
  };

  const handleConfirmDeletePair = async (index: number, id?: number) => {
    if (id !== undefined) {
      try {
        const reqData = {
          modelName: "themesintro",
          id: id,
        };

        const response = await postRequest(
          `${baseURL}/lesson/delete-bretzel-content`,
          reqData
        );
        if (response?.data) {
          const updatedPairs = [...pairs];
          updatedPairs.splice(index, 1);
          setPairs(updatedPairs);
          Toast(response?.data.statusMessage, "success");
        } else {
          console.error("Error deleting pair:", response?.data);
        }
      } catch (error) {
        console.error("Error deleting pair:", error);
      }
    } else {
      const updatedPairs = [...pairs];
      updatedPairs.splice(index, 1);
      setPairs(updatedPairs);
    }
  };

  const handleChooseFileClick = (inputType: string) => {
    const inputRef =
      inputType === "cover"
        ? fileInputRef
        : inputType === "startingLesson"
          ? startingLessonInputRef
          : myLessonsInputRef;
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleStartingLessonImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setStartingLessonImageLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      const response = await multiPostRequest(
        `${baseURL}/file-upload`,
        formData
      );

      if (response?.data.success) {
        setStartingLessonImage(response?.data?.data[0].uri);
      }
      setStartingLessonImageLoading(false);
    }
  };

  // const handleMyLessonsImageChange = async (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const file = event.target.files?.[0];
  //   if (file) {
  //     // setMyLessonsImageLoading(true);
  //     const formData = new FormData();
  //     formData.append("file", file);
  //     const response = await multiPostRequest(
  //       `${baseURL}/file-upload`,
  //       formData
  //     );

  //     if (response?.data.success) {
  //       setMyLessonsImage(response?.data?.data[0].uri);
  //     }
  //     // setMyLessonsImageLoading(false);
  //   }
  // };

  const handleDeleteImage = async (imageType: string) => {
    let fileUri = "";

    switch (imageType) {
      // case "cover":
      //   fileUri = coverImage;
      //   break;
      case "startingLesson":
        fileUri = startingLessonImage;
        break;
      case "myLessons":
        fileUri = myLessonsImage;
        break;
      default:
        return;
    }

    try {
      const response = await postRequest(`${baseURL}/remove-file`, {
        fileUri,
      });

      if (response?.data.success) {
        switch (imageType) {
          // case "cover":
          //   setCoverImage("");
          //   break;
          case "startingLesson":
            setStartingLessonImage("");
            break;
          // case "myLessons":
          //   setMyLessonsImage("");
          //   break;
          default:
            break;
        }
      } else {
        console.error("Failed to delete the image");
      }
    } catch (error) {
      console.error("Error deleting image:", error);
    }
  };

  useEffect(() => {
    let timeLine = gsap.timeline();
    timeLine.from(".row-container", {
      opacity: 0,
      y: -40,
      duration: 1,
      ease: "power2.out",
    });
  }, [isLoading]);

  return (
    <div className="container-fluid">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <div className="p-3 main-page">
            <div
              className="d-flex  align-items-center go-back"
              onClick={handleBackClick}
              style={{ width: "fit-content" }}
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                className="me-2"
                style={{
                  backgroundColor: "#D9D9D9",
                  padding: "7px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  color: "#000",
                }}
              />
              <label
                style={{
                  color: "#FF9E00",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                Back
              </label>
            </div>
            <Label
              fontSize="22px"
              alignItems="center"
              display="flex"
              justifyContent="center"
              color="#7542E4"
              fontWeight={700}
              className="row-container"
            >
              THEME NAME [{parentThemeName}]
            </Label>
            <VerticalSpace height={20} />

            <div className="row  p-3 d-flex main-container justify-content-between">
              <div className="col lesson-container left-container">
                <div
                  className="container p-4"
                  style={{
                    backgroundColor: "rgb(220 220 220 / 66%)",
                    borderRadius: "30px",
                  }}
                >
                  {/* ── sm+: [Intro Title ←→ Free Theme + toggle] on one row
                         sm-: Free Theme + toggle (full row), then Intro Title (full row), then editor ── */}

                  {/* Row 1 on sm+ / Row 1 on sm (Free Theme) */}
                  <div className="d-flex flex-wrap justify-content-sm-between align-items-sm-center mb-2"
                    style={{ gap: "20px" }}>

                    <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                      <input
                        type="checkbox"
                        id="isSelected"
                        checked={isSelected}
                        onChange={(e) => setIsSelected(e.target.checked)}
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                          accentColor: "#7542E4",
                        }}
                      />
                      <Label fontSize="16px" fontWeight={700}>
                        Choose your first home lesson
                      </Label>
                    </div>
                    
                    <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center" style={{ gap: "20px" }}>
                      <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                        <Label fontSize="16px" fontWeight={700}>
                          Free Theme
                        </Label>
                        <Switch
                          isOn={isFree}
                          onToggle={() => setIsFree((prev: boolean) => !prev)}
                        />
                      </div>
                      
                      <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                        <Label fontSize="16px" fontWeight={700}>
                          Home with some knowledge
                        </Label>
                        <Switch
                          isOn={isContinueLearning}
                          onToggle={() => setIsContinueLearning((prev: boolean) => !prev)}
                        />
                      </div>
                      
                      <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                        <Label fontSize="16px" fontWeight={700}>
                          Home with cero
                        </Label>
                        <Switch
                          isOn={isFreshLearning}
                          onToggle={() => setIsFreshLearning((prev: boolean) => !prev)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* TextEditor — always full width */}
                  <div className="w-100 mt-4">
                    <Label fontSize="20px" fontWeight={700}>
                      Intro Title
                    </Label>
                    <TextEditor
                      ref={introTitleEditorRef}
                      value={introTitle}
                      onChange={handleIntroTitle}
                      height="auto"
                    />
                    {errors.introTitle && (
                      <span style={{ color: "red", fontSize: "12px" }}>
                        {errors.introTitle}
                      </span>
                    )}
                  </div>
                  {/* <div className="col-12">
                    <Label fontSize="20px" fontWeight={700}>
                      Intro
                    </Label>{" "}
                    <VerticalSpace height={10} />
                    <TextEditor
                      ref={introEditorRef}
                      value={intro}
                      onChange={handleIntro}
                      height="auto"
                    />
                    {errors.intro && (
                      <span style={{ color: "red", fontSize: "12px" }}>
                        {errors.intro}
                      </span>
                    )}
                  </div> */}
                  <VerticalSpace height={10} />
                  <div className="col-12">
                    {!startingLessonImage && (
                      <div className="col-12">
                        <Label fontSize="20px" fontWeight={700}>
                          Starting Theme Image
                        </Label>
                        <VerticalSpace height={4} />
                        <div className="drop_box">
                          {startingLessonImageLoading ? (
                            <RowContainer
                              alignItem="center"
                              justifyContent="center"
                              padding="30px"
                            >
                              <Loader />
                            </RowContainer>
                          ) : (
                            <>
                              <header>
                                <h4 className="image-text">
                                  Upload a&nbsp;
                                  <span style={{ color: "#7542E4" }}>
                                    starting lesson
                                  </span>
                                  &nbsp;image for the lesson section
                                </h4>
                                <h4>jpg, png or svg</h4>
                              </header>

                              <input
                                type="file"
                                hidden
                                ref={startingLessonInputRef}
                                className={`form-control ${errors.startingLessonImage ? "is-invalid" : ""
                                  }`}
                                accept="image/*"
                                onChange={handleStartingLessonImageChange}
                              />
                              <VerticalSpace height={10} />
                              <button
                                type="button"
                                className="user"
                                style={{
                                  color: "#100F15",
                                  background: "none",
                                  border: "1px solid #100F15",
                                  borderRadius: "10px",
                                  padding: "14px 30px",
                                  fontSize: "14px",
                                  width: "auto",
                                }}
                                onClick={() =>
                                  handleChooseFileClick("startingLesson")
                                }
                              >
                                Choose file
                              </button>
                              {errors.startingLessonImage && (
                                <div className="invalid-feedback  d-flex justify-content-center">
                                  {errors.startingLessonImage}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    {startingLessonImage && (
                      <div className="col-12">
                        <Label fontSize="20px" fontWeight={700}>
                          Starting Lesson Image
                        </Label>
                        <VerticalSpace height={4} />
                        <div
                          className="drop_box"
                          style={{
                            flexDirection: "row",
                            justifyContent: "space-between",
                          }}
                        >
                          {startingLessonImageLoading ? (
                            <RowContainer
                              alignItem="center"
                              justifyContent="center"
                              padding="30px"
                            >
                              <Loader />
                            </RowContainer>
                          ) : (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                }}
                              >
                                {typeof startingLessonImage === "string" ? (
                                  <>
                                    <img
                                      src={startingLessonImage}
                                      alt="starting-lessons-image"
                                      style={{
                                        maxWidth: "100px",
                                        maxHeight: "100px",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        handleChooseFileClick("startingLesson")
                                      }
                                    />
                                    <input
                                      type="file"
                                      hidden
                                      ref={startingLessonInputRef}
                                      accept="image/*"
                                      onChange={handleStartingLessonImageChange}
                                    />
                                  </>
                                ) : (
                                  <img
                                    src={URL.createObjectURL(
                                      startingLessonImage
                                    )}
                                    alt="starting-lessons-image"
                                    style={{
                                      maxWidth: "100px",
                                      maxHeight: "100px",
                                    }}
                                  />
                                )}
                                {/* <p
                                style={{
                                  color: "#000000",
                                  marginTop: "0%",
                                  marginBottom: "0%",
                                }}
                              >
                                {startingLessonImage.name}
                              </p>{" "} */}
                              </div>
                              <div
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  handleDeleteImage("startingLesson");
                                }}
                              >
                                <img
                                  src={deleteIcon}
                                  className="user-scale"
                                  alt="Delete"
                                  style={{
                                    WebkitUserSelect: "none",
                                    userSelect: "none",
                                  }}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    {/* <VerticalSpace height={10} />
                    {!myLessonsImage && (
                      <div className="col-12">
                        <Label fontSize="20px" fontWeight={700}>
                          My Lessons Image
                        </Label>
                        <VerticalSpace height={4} />
                        <div className="drop_box">
                          {myLessonsImageLoading ? (
                            <RowContainer
                              alignItem="center"
                              justifyContent="center"
                              padding="30px"
                            >
                              <Loader />
                            </RowContainer>
                          ) : (
                            <>
                              <header>
                                <h4 className="image-text">
                                  Upload a&nbsp;
                                  <span style={{ color: "#7542E4" }}>
                                    my lessons
                                  </span>
                                  &nbsp;image for the lesson section
                                </h4>
                                <h4>jpg, png or svg</h4>
                              </header>

                              <input
                                type="file"
                                hidden
                                ref={myLessonsInputRef}
                                className={`form-control ${
                                  errors.myLessonsImage ? "is-invalid" : ""
                                }`}
                                accept="image/*"
                                onChange={handleMyLessonsImageChange}
                              />
                              <VerticalSpace height={10} />
                              <button
                                type="button"
                                className="user"
                                style={{
                                  color: "#100F15",
                                  background: "none",
                                  border: "1px solid #100F15",
                                  borderRadius: "10px",
                                  padding: "14px 30px",
                                  fontSize: "14px",
                                  width: "auto",
                                }}
                                onClick={() =>
                                  handleChooseFileClick("myLessons")
                                }
                              >
                                Choose file
                              </button>
                              {errors.myLessonsImage && (
                                <div className="invalid-feedback  d-flex justify-content-center">
                                  {errors.myLessonsImage}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    {myLessonsImage && (
                      <div className="col-12">
                        <Label fontSize="20px" fontWeight={700}>
                          My Lessons Image
                        </Label>
                        <VerticalSpace height={4} />
                        <div
                          className="drop_box"
                          style={{
                            flexDirection: "row",
                            justifyContent: "space-between",
                          }}
                        >
                          {myLessonsImageLoading ? (
                            <RowContainer
                              alignItem="center"
                              justifyContent="center"
                              padding="30px"
                            >
                              <Loader />
                            </RowContainer>
                          ) : (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                }}
                              >
                                {typeof myLessonsImage === "string" ? (
                                  <>
                                    <img
                                      src={myLessonsImage}
                                      alt="my-lessoms-image"
                                      style={{
                                        maxWidth: "100px",
                                        maxHeight: "100px",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        handleChooseFileClick("myLessons")
                                      }
                                    />
                                    <input
                                      type="file"
                                      hidden
                                      ref={myLessonsInputRef}
                                      accept="image/*"
                                      onChange={handleMyLessonsImageChange}
                                    />
                                  </>
                                ) : (
                                  <img
                                    src={URL.createObjectURL(myLessonsImage)}
                                    alt="my-lessoms-image"
                                    style={{
                                      maxWidth: "100px",
                                      maxHeight: "100px",
                                    }}
                                  />
                                )}
                                {/* <p
                                style={{
                                  color: "#000000",
                                  marginTop: "0%",
                                  marginBottom: "0%",
                                }}
                              >
                                {myLessonsImage.name}
                              </p>{" "} */}
                    {/*  </div>
                              <div
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  handleDeleteImage("myLessons");
                                }}
                              >
                                <img
                                  src={deleteIcon}
                                  className="user-scale"
                                  alt="Delete"
                                  style={{
                                    WebkitUserSelect: "none",
                                    userSelect: "none",
                                  }}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )} 
                     */}
                  </div>
                  {/* <VerticalSpace height={10} />
                  <div className="col-12 d-flex align-items-center lessons-top justify-content-between gap-2">
                    <Label fontSize="20px" fontWeight={700}>
                      Is image have white bg
                    </Label>{" "}
                    <div className="d-flex" style={{ gap: "10px" }}>
                      <RadioButton
                        checked={isWhiteBackground === true}
                        onChange={toggleSwitch}
                      />
                      <Label fontSize="14px" fontWeight={400}>
                        True
                      </Label>
                      <RadioButton
                        checked={isWhiteBackground === false}
                        onChange={toggleSwitch}
                      />
                      <Label fontSize="14px" fontWeight={400}>
                        False
                      </Label>
                    </div>
                  </div> */}
                  <VerticalSpace height={20} />
                  <div className="col-12">
                    <Label fontSize="20px" fontWeight={700}>
                      Partículas
                    </Label>
                    <VerticalSpace height={10} />
                    <div
                      className="col d-flex flex-column align-itmes-center particle-example"
                      style={{ padding: "0px 14px 0px 0px", gap: "16px" }}
                    >
                      {pairs.map((pair, index) => (
                        <div
                          key={index}
                          className="row particle-row"
                          style={{
                            flexWrap: "unset",
                            alignItems: "center",
                          }}
                        >
                          <div
                            className="col particle-dropdown"
                            style={{ flex: "1", minWidth: "0" }}
                          >
                            <NestedMenu
                              data={
                                pair.dropdownValue ? [pair.dropdownValue!] : []
                              }
                              onSelectItem={(value) =>
                                handleDropdownChange(
                                  index,
                                  value as DropdownValue
                                )
                              }
                              borderColor={
                                validationError.pairs[index]?.dropdownValue
                                  ? "#dc3545"
                                  : "#D9D9D9"
                              }
                            />
                            <div
                              className="p-0 delete-icon"
                              style={{
                                cursor: "pointer",
                                width: "fit-content",
                              }}
                              onClick={() => handleDeletePair(index, pair.id)}
                            >
                              <img
                                src={deleteIcon}
                                className="user-scale"
                                style={{
                                  WebkitUserSelect: "none",
                                  userSelect: "none",
                                }}
                              />
                            </div>
                          </div>
                          <div className="col particle-input">
                            <input
                              type="text"
                              value={pair.inputValue || ""}
                              onChange={(e) =>
                                handleInputChange(index, e.target.value)
                              }
                              onKeyDown={(e) => handleKeyDown(index, e)}
                              className={`form-control ${validationError.pairs[index]?.inputValue
                                ? "is-invalid"
                                : ""
                                }`}
                              placeholder=""
                              style={{
                                borderRadius: "10px",
                                height: "40px",
                                fontSize: "14px",
                                backgroundColor: pair.isReadOnly
                                  ? "#ffffff00"
                                  : "",
                                border: pair.isReadOnly
                                  ? "1px solid #D9D9D9"
                                  : "",
                              }}
                              readOnly={pair.isReadOnly}
                            />
                          </div>
                          <div
                            className="p-0 delete-button"
                            style={{
                              cursor: "pointer",
                              width: "fit-content",
                            }}
                            onClick={() => handleDeletePair(index, pair.id)}
                          >
                            <img src={deleteIcon} className="user-scale" />
                          </div>
                        </div>
                      ))}
                    </div>
                    <VerticalSpace height={16} />

                    <button
                      type="button"
                      className="btn btn-outline-secondary me-2 text-center user"
                      style={{
                        color: "#100F15",
                        background: "none",
                        border: "1px solid #100F15",
                        borderRadius: "10px",
                        padding: "14px 40px",
                        fontSize: "14px",
                        width: "auto",
                      }}
                      onClick={addNewPair}
                    >
                      Add new
                    </button>
                  </div>
                </div>
              </div>
              <HorizontalSpace width={5} />
              <div className="col theme-container right-container">
                <div
                  className="container p-4"
                  style={{
                    backgroundColor: "rgb(220 220 220 / 66%)",
                    borderRadius: "30px",
                  }}
                >
                  <div className="col-12 theme-content">
                    <div className="d-flex justify-content-between align-items-center">
                      <Label fontSize="20px" fontWeight={700}>
                        Content
                      </Label>
                      {/* Bulk Copy / Cut toolbar — visible only when sub-themes are selected */}
                      {selectedSubThemes.length > 0 && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <button
                            type="button"
                            className="btn user"
                            title={`Copy ${selectedSubThemes.length} selected sub-theme(s)`}
                            style={{
                              width: "100px",
                              backgroundColor: "#DAD7FE",
                              color: "#000",
                              borderRadius: 8,
                              fontSize: 12,
                              padding: "5px 12px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 5,
                            }}
                            onClick={handleCopySelectedSubThemes}
                          >
                            <img
                              src={clipboardIcon}
                              alt="copy"
                              style={{ width: 20, height: 20 }}
                            />
                            Copy ({selectedSubThemes.length})
                          </button>
                          <button
                            type="button"
                            className="btn user"
                            title={`Cut ${selectedSubThemes.length} selected sub-theme(s)`}
                            style={{
                              width: "100px",
                              backgroundColor: "#DAD7FE",
                              color: "#000",
                              borderRadius: 8,
                              fontSize: 12,
                              padding: "5px 12px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 5,
                            }}
                            onClick={handleCutSelectedSubThemes}
                          >
                            <img
                              src={cutIcon}
                              alt="cut"
                              style={{ width: 20, height: 20 }}
                            />
                            Cut ({selectedSubThemes.length})
                          </button>
                          <button
                            type="button"
                            className="btn user"
                            title="Clear selection"
                            style={{
                              width: "100px",
                              background: "none",
                              border: "1px solid #aaa",
                              borderRadius: 8,
                              fontSize: 12,
                              padding: "5px 10px",
                              color: "#555",
                            }}
                            onClick={() => setSelectedSubThemes([])}
                          >
                            ✕ Clear
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Clipboard paste banner — only visible when subtheme items are in clipboard */}
                    {clipboard.itemType === "subtheme" && clipboard.items.length > 0 && (
                      <div
                        style={{
                          background: clipboard.action === "cut" ? "#fff3cd" : "#e8f4fd",
                          border: `1px solid ${clipboard.action === "cut" ? "#ffc107" : "#7542E4"}`,
                          borderRadius: 8,
                          padding: "8px 12px",
                          marginBottom: 10,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 8,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>
                            <img src={pasteIcon} alt="" />
                            {clipboard.action === "cut" ? "Cut" : "Copied"}:&nbsp;
                          </span>
                          <span style={{ fontSize: 12, color: "#555" }}>
                            {clipboard.items.map((i) => i.name).join(", ")}
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            type="button"
                            className="btn user"
                            disabled={isPasting}
                            style={{
                              backgroundColor: "#7542E4",
                              color: "#fff",
                              borderRadius: 8,
                              fontSize: 12,
                              padding: "5px 14px",
                            }}
                            onClick={handlePasteSubThemes}
                          >
                            {isPasting
                              ? "Pasting…"
                              : `Paste ${clipboard.action === "cut" ? "(Move)" : "(Copy)"} here`}
                          </button>
                          <button
                            type="button"
                            className="btn user"
                            style={{
                              background: "none",
                              border: "1px solid #aaa",
                              borderRadius: 8,
                              fontSize: 12,
                              padding: "5px 10px",
                              color: "#555",
                            }}
                            onClick={() => dispatch(clearClipboard())}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    )}

                    <div>
                      {subthemes.length === 0 ? (
                        <div className="p-5 d-flex justify-content-center align-items-center ">
                          <label
                            htmlFor="theme"
                            className="form-label"
                            style={{ fontWeight: "bold", color: "#7542E4" }}
                          >
                            There are no sub-themes yet in this theme.
                          </label>
                        </div>
                      ) : (
                        subthemes.map((subtheme) => (
                          <div
                            className="row d-flex justify-content-center align-items-center"
                            style={{
                              gap: "10px",
                              opacity:
                                clipboard.action === "cut" &&
                                  clipboard.itemType === "subtheme" &&
                                  clipboard.items.some((ci) => ci.id === subtheme.id)
                                  ? 0.4
                                  : 1,
                            }}
                            key={subtheme.id}
                          >
                            {/* Checkbox for bulk selection */}
                            <div
                              className="p-0"
                              style={{ width: "fit-content", cursor: "pointer" }}
                              onClick={(e) => { e.stopPropagation(); toggleSubThemeSelection(subtheme.id); }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedSubThemes.includes(subtheme.id)}
                                onChange={() => toggleSubThemeSelection(subtheme.id)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  width: 16,
                                  height: 16,
                                  accentColor: "#7542E4",
                                  cursor: "pointer",
                                }}
                              />
                            </div>
                            <div
                              className="drop_box theme-box col-8 user"
                              style={{
                                flexDirection: "row",
                                justifyContent: "space-between",
                                cursor: "pointer",
                                outline: selectedSubThemes.includes(subtheme.id)
                                  ? "2px solid #7542E4"
                                  : "none",
                              }}
                              onClick={() => handleEditSubTheme(subtheme)}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                }}
                              >
                                <Label
                                  fontSize="14px"
                                  color="#000"
                                  fontWeight={400}
                                >
                                  {subtheme.name}
                                </Label>
                              </div>

                              <div style={{ cursor: "pointer" }}>
                                <Label
                                  fontSize="16px"
                                  color="#000"
                                  fontWeight={400}
                                  cursor="pointer"
                                  padding="8px 13px 8px 13px"
                                  bgColor="#DAD7FE"
                                  justifyContent="center"
                                  display="flex"
                                  borderRadius="40px"
                                  className="user"
                                >
                                  {subtheme.cards} cards
                                </Label>
                              </div>
                            </div>
                            {/* Duplicate in-place */}
                            <div
                              className="p-0"
                              title="Duplicate here"
                              style={{ cursor: "pointer", width: "fit-content" }}
                              onClick={() => handleDuplicateSubTheme(subtheme.id)}
                            >
                              <img
                                src={copyIcon}
                                className="user-scale"
                                alt="Duplicate"
                                style={{ WebkitUserSelect: "none", userSelect: "none" }}
                              />
                            </div>
                            {/* Copy to clipboard */}
                            <div
                              className="p-0"
                              title="Copy to clipboard"
                              style={{ cursor: "pointer", width: "fit-content" }}
                              onClick={() => handleCopySubTheme(subtheme.id)}
                            >
                              <img
                                src={clipboardIcon}
                                className="user-scale"
                                alt="Copy"
                                style={{
                                  WebkitUserSelect: "none",
                                  userSelect: "none",
                                }}
                              />
                            </div>
                            {/* Cut to clipboard */}
                            <div
                              className="p-0"
                              title="Cut to clipboard"
                              style={{ cursor: "pointer", width: "fit-content" }}
                              onClick={() => handleCutSubTheme(subtheme.id)}
                            >
                              <img
                                src={cutIcon}
                                className="user-scale"
                                alt="Cut"
                                style={{ WebkitUserSelect: "none", userSelect: "none" }}
                              />
                            </div>
                            <div
                              className="p-0"
                              style={{
                                cursor: "pointer",
                                width: "fit-content",
                              }}
                              onClick={() =>
                                handleAddFlashcard(subtheme.id, subtheme.name)
                              }
                            >
                              <img
                                src={editIcon}
                                className="user-scale"
                                style={{
                                  WebkitUserSelect: "none",
                                  userSelect: "none",
                                }}
                              />
                            </div>
                            <div
                              className="p-0"
                              style={{
                                cursor: "pointer",
                                width: "fit-content",
                              }}
                              onClick={() =>
                                handleDeleteSubTheme(subtheme.id, subtheme.name)
                              }
                            >
                              <img
                                src={deleteIcon}
                                className="user-scale"
                                style={{
                                  WebkitUserSelect: "none",
                                  userSelect: "none",
                                }}
                              />
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <VerticalSpace height={12} />
                    <button
                      type="button"
                      className="user"
                      style={{
                        color: "#100F15",
                        background: "none",
                        border: "1px solid #100F15",
                        alignItems: "center",
                        borderRadius: "10px",
                        padding: "14px 40px",
                        width: "auto",
                      }}
                      onClick={handleNewSubThemes}
                    >
                      Add sub-theme
                    </button>
                  </div>
                </div>
              </div>
              <VerticalSpace height={200} />
              <div
                className="container row d-flex justify-content-center publish-button"
                style={{
                  position: "fixed",
                  height: "12%",
                  bottom: "0px",
                  zIndex: 1,
                  background:
                    "linear-gradient(178.35deg, rgba(255, 255, 255, 0) 1.4%, rgba(255, 255, 255, 0.69) 26.79%, #FFFFFF 50.22%)",
                }}
              >
                <div
                  className="d-flex justify-content-center"
                  style={{
                    position: "fixed",
                    zIndex: 1,
                    bottom: "34px",

                    backgroundColor: "#FFFFFF",
                  }}
                >
                  {" "}
                  <button
                    type="button"
                    className="btn btn-success d-flex justify-content-center user"
                    style={{
                      backgroundColor: "#FF9E00",
                      color: "#100F15",
                      borderRadius: "10px",
                      height: "fit-content",
                      padding: "10px 20%",
                      width: "400px",
                      fontSize: "14px",
                    }}
                    onClick={handlePublishClick}
                  >
                    Publish
                  </button>
                </div>
              </div>
            </div>
          </div>

          {addSubThemes && (
            <div
              className="modal"
              role="dialog"
              style={{
                display: "block",
                position: "fixed",
                zIndex: 1,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
              }}
            >
              <div className="modal-dialog">
                <div className="modal-content" style={{ borderRadius: "20px" }}>
                  <div className="modal-body p-3">
                    <h5 style={{ fontWeight: "bold" }}>Add a sub-theme</h5>
                    <VerticalSpace height={10} />
                    <input
                      type="text"
                      className="form-control"
                      id="inputthemes"
                      placeholder="New sub-theme name"
                      value={themeName}
                      onChange={(e) => {
                        setThemeName(e.target.value);
                        if (subThemeError && e.target.value) {
                          setSubThemeError("");
                        }
                      }}
                    />
                    {subThemeError && (
                      <div className="error-message">{subThemeError}</div>
                    )}
                  </div>
                  <div className="modal-body d-flex justify-content-between">
                    <button
                      type="button"
                      className="btn btn-secondary user"
                      onClick={() => setNewThemes(false)}
                      style={{
                        padding: "12px",
                        color: "#100F15",
                        background: "none",
                        border: "1px solid #100F15",
                        alignItems: "center",
                        borderRadius: "10px",
                        justifyContent: "center",
                        display: "flex",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger user"
                      style={{
                        backgroundColor: "#FF9E00",
                        borderRadius: "10px",
                        color: "#100F15",
                      }}
                      onClick={handleSaveSubTheme}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {showDeleteDialog && (
        <DeleteConfirmation
          showDeletePopup={true}
          setShowDeletePopup={setShowDeleteDialog}
          message={deleteDialogMessage}
          deleteButtonLabel="Delete"
          onDelete={onConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}

      {loading && <DeleteLoader />}
      <VerticalSpace height={120} />
    </div>
  );
};

export default SubThemes;
